import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import type { AcceptEnum } from '@commons/bo-ui/form/file-picker/models/accept.enum';
import type { FilePickerOptions } from '@commons/bo-ui/form/file-picker/models/file-picker-options.interface';
import type { FileResolvable } from '@commons/bo-ui/form/file-picker/models/file-picker.types';
import type { RejectedFilesCount } from '@commons/bo-ui/form/file-picker/models/rejected-files-count';
import type { FileWebServiceInterface } from '@commons/bo-ui/form/file-picker/services/file.web-service.interface';
import type { Observable } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  inject,
  Input,
} from '@angular/core';
import { FormArray, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { FileActionsComponent } from '@commons/bo-ui/form/file-picker/components/file-actions/file-actions.component';
import { FileUploaderComponent } from '@commons/bo-ui/form/file-picker/components/file-uploader/file-uploader.component';
import { PlaceholderComponent } from '@commons/bo-ui/form/file-picker/components/placeholder/placeholder.component';
import { SpinnerComponent } from '@commons/bo-ui/form/file-picker/components/spinner/spinner.component';
import { FILE_PICKER_DEFAULT_OPTIONS } from '@commons/bo-ui/form/file-picker/models/file-picker-options.interface';
import { UploadStep } from '@commons/bo-ui/form/file-picker/models/upload-step.enum';
import { FileSizePipe } from '@commons/bo-ui/form/file-picker/pipes/file-size.pipe';
import { FILE_PICKER_SERVICE_TOKEN } from '@commons/bo-ui/form/file-picker/services/file-picker-service.token';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, map, noop, of, switchMap } from 'rxjs';

/**
 * This component is intended to help user to:
 *   - pick files from file system,
 *   - delete a file after a deletion confirmation,
 *   - preview the selected or given file (single upload only),
 *   - be able to crop the image after picking it (single upload only ; use the enableCropper option to enable it).
 *
 * It can be use as a form control in a form. See example below.
 *
 * `HTML`
 * ```html
 *   <form>
 *     <app-file-picker
 *      [formControl]="formControl"
 *      name="logoFile"
 *      [options]="options"
 *    >{{ options.title }}</app-file-picker>
 *   </form>
 * ```
 * `TS`
 * ```ts
 *  const options: Partial<FilePickerOptions> = {
 *    acceptedFormats: [AcceptEnum.JPG, AcceptEnum.PNG],
 *    title: 'Mon logo',
 *    caption: 'Déposez votre logo ici',
 *    maxSizePerFileInMB: 0.48828,
 *  };
 * ```
 * \
 * Its default options are:
 * ```ts
 * const FILE_PICKER_DEFAULT_OPTIONS: FilePickerOptions = {
 *   acceptedFormats: [AcceptEnum.JPG, AcceptEnum.PNG, AcceptEnum.SVG, AcceptEnum.PDF],
 *   maxSizePerFileInMB: 5,
 *   hideTitle: false,
 *   enableCropper: false,
 *   publicUpload: true,
 *   additionalAllowedApps: [],
 *   additionalAllowedRoles: [],
 *   additionalAllowedUsers: [],
 *   multiUpload: false,
 * }
 * ```
 * \
 * For private files, you must specify the `publicUpload` option to false and add an array of additional allowed roles if needed, see the example below.
 * ```ts
 *  const options: Partial<FilePickerOptions> = {
 *    publicUpload: false,
 *    additionalAllowedRoles: ['ROLE_OFFICE_MANAGER'],
 *  };
 * ```
 */
@Component({
  selector: 'app-file-picker',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MainComponent),
      multi: true,
    },
  ],
  imports: [
    PlaceholderComponent,
    FileActionsComponent,
    SpinnerComponent,
    FileUploaderComponent,
    ReactiveFormsModule,
    AsyncPipe,
  ],
})
export class MainComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  private static count = 0;
  #fileUrls!: string[];
  isDisabled = false;

  @Input() options: Partial<FilePickerOptions> = {};

  readonly #cdr = inject(ChangeDetectorRef);
  readonly #elementRef = inject(ElementRef);
  readonly #fileWebservice: FileWebServiceInterface = inject(FILE_PICKER_SERVICE_TOKEN);

  @HostBinding('id') id: string;

  rejectedFileCount: RejectedFilesCount = {
    maxSizeExceeded: 0,
    fileTooSmall: 0,
  };

  filesControlsArray = new FormArray<FormControl>([]);
  computedAccept: string | undefined;

  readonly #uploadStep$ = new BehaviorSubject<UploadStep>(UploadStep.Waiting);
  readonly #hasPreviewableImage$ = new BehaviorSubject<boolean>(false);
  #previewImageUrl$!: Observable<string | null>;

  vm$!: Observable<{
    uploadStep: UploadStep;
    canPreviewImage: boolean;
    previewImageUrl: string;
  }>;

  UploadStep = UploadStep;

  get fileUrls(): string[] {
    return this.#fileUrls;
  }

  set fileUrls(urls: string[]) {
    this.#fileUrls = urls;
    this.onChange(urls);
    this.onTouched();
  }

  get fileMaxWeightLabel(): string {
    const fileMaxSizeString = new FileSizePipe().transform(this.options.maxSizePerFileInMB! * 1024 * 1024);
    const labelMapping = {
      multiUpload: {
        error:
          this.rejectedFileCount.maxSizeExceeded === 1
            ? `Taille maximale dépassée par un fichier (maximum ${fileMaxSizeString})`
            : `Taille maximale dépassée par ${this.rejectedFileCount.maxSizeExceeded} fichiers (maximum ${fileMaxSizeString})`,
        default: `Taille maximale par fichier\u00A0: ${fileMaxSizeString}`,
      },
      soloUpload: {
        error: `Taille maximale du fichier dépassée (maximum ${fileMaxSizeString})`,
        default: `Taille maximale du fichier\u00A0: ${fileMaxSizeString}`,
      },
    };
    const multiOrSolo = this.options.multiUpload ? 'multiUpload' : 'soloUpload';
    const defaultOrError = this.rejectedFileCount.maxSizeExceeded ? 'error' : 'default';
    return labelMapping[multiOrSolo][defaultOrError];
  }

  get fileSizeLabel(): string {
    const requiredSize = `${this.options.requiredSize!.width}px * ${this.options.requiredSize!.height}px`;

    if (this.options.requiredSize && this.rejectedFileCount.fileTooSmall === 0) {
      return requiredSize;
    }

    return `${
      this.options.multiUpload ? `${this.rejectedFileCount.fileTooSmall} fichiers trop petits` : 'Fichier trop petit'
    } (taille demandée: ${this.options.requiredSize!.width}px * ${this.options.requiredSize!.height}px)`;
  }

  get hasErrors(): boolean {
    return this.rejectedFileCount.maxSizeExceeded > 0 || this.rejectedFileCount.fileTooSmall > 0;
  }

  get filesControls(): FormControl[] {
    return this.filesControlsArray.controls;
  }

  get isUploading(): boolean {
    return this.filesControls.some((control) => typeof control.value !== 'string');
  }

  get globalProgress(): number {
    const allFilesCount = this.filesControls.length;
    const uploadedFilesCount = this.filesControls.filter(({ value }) => typeof value === 'string').length;
    return (uploadedFilesCount / allFilesCount) * 100;
  }

  get isPreviewImageSvg(): boolean {
    return !!this.fileUrls?.[0].endsWith('.svg');
  }

  get hasDownloadError(): boolean {
    return this.filesControls.some((fileControl) => fileControl.hasError('downloadError'));
  }

  get minimumSizeLabel(): string | null {
    if (!this.options.minimumSize) {
      return null;
    }

    return `Dimensions minimales : ${this.options.minimumSize}`;
  }

  onChange: (fileUrls: string[]) => void = noop;
  onTouched: () => void = noop;

  constructor() {
    this.id = `file-picker__${++MainComponent.count}`;
  }

  ngOnInit(): void {
    this.#populateOptions();
    this.filesControlsArray.valueChanges.subscribe({
      next: (filesResolvables: FileResolvable[]) => {
        if (!filesResolvables.length) {
          this.#uploadStep$.next(UploadStep.Waiting);
          this.fileUrls = [];
        } else if (filesResolvables.every((fileResolvable) => typeof fileResolvable === 'string')) {
          this.#uploadStep$.next(UploadStep.Ready);
          if (this.filesControlsArray.valid) {
            this.fileUrls = filesResolvables;
          }
        }
        this.#hasPreviewableImage$.next(this.#canBePreviewImage(filesResolvables[0]) && !this.hasDownloadError);
      },
    });

    this.#previewImageUrl$ = this.#hasPreviewableImage$.pipe(
      distinctUntilChanged(),
      switchMap((hasPreviewableImage) =>
        hasPreviewableImage && this.fileUrls?.length && !!this.fileUrls[0]
          ? this.#fileWebservice.getFileUrl(this.fileUrls[0])
          : of(null)
      ),
      catchError(() => {
        if (this.#hasPreviewableImage$.value) {
          this.#hasPreviewableImage$.next(false);
        }
        return of(null);
      })
    );

    this.vm$ = combineLatest([
      this.#uploadStep$,
      this.#hasPreviewableImage$.pipe(map((hasPreviewableImage) => hasPreviewableImage && !this.options.multiUpload)),
      this.#previewImageUrl$,
    ]).pipe(
      map(([uploadStep, canPreviewImage, previewImageUrl]) => ({
        uploadStep,
        canPreviewImage,
        previewImageUrl: previewImageUrl!,
      }))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.#populateOptions();
    }
  }

  ngOnDestroy(): void {
    this.#uploadStep$.complete();
    this.#hasPreviewableImage$.complete();
  }

  writeValue(fileUrls: string[] | undefined): void {
    this.filesControlsArray.clear();
    if (fileUrls?.length) {
      fileUrls.forEach((fileUrl) => {
        this.filesControlsArray.push(new FormControl(fileUrl));
      });
    }
    this.#clearNullControl();
  }

  registerOnChange(onChangeFn: (fileUrls: string[]) => void): void {
    this.onChange = (urls) => {
      onChangeFn(urls);
      this.#cdr.markForCheck();
    };
  }

  registerOnTouched(onTouchedFn: () => void): void {
    this.onTouched = () => {
      onTouchedFn();
      this.#cdr.markForCheck();
    };
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
    this.#cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  doOutsideClick(event: MouseEvent): void {
    if (!this.#elementRef.nativeElement.contains(event.target)) {
      this.onTouched();
    }
  }

  onFilesChosen(files: File[]): void {
    files.forEach((file) => {
      this.filesControlsArray.push(new FormControl(file));
    });
    this.#uploadStep$.next(UploadStep.Uploading);
  }

  onFileCancelled(index: number): void {
    this.filesControlsArray.removeAt(index);
  }

  onPreviewImageError(): void {
    if (this.#hasPreviewableImage$.value) {
      this.#hasPreviewableImage$.next(false);
    }
  }

  onFilesInput(event: Event): void {
    const chosenFiles: FileList = (event.target as HTMLInputElement).files!;
    const candidateFiles: File[] = [];
    for (const chosenFile of chosenFiles) {
      if (this.#isFileTypeAccepted(chosenFile)) {
        candidateFiles.push(chosenFile);
      }
    }
    const acceptedFiles = candidateFiles.filter((file) => !this.#isFileSizeLimitExceeded(file));
    this.rejectedFileCount.maxSizeExceeded = candidateFiles.length - acceptedFiles.length;

    if (acceptedFiles.length) {
      this.onFilesChosen(acceptedFiles);
    }
  }

  #populateOptions(): void {
    this.options = {
      ...FILE_PICKER_DEFAULT_OPTIONS,
      ...this.options,
    };
    this.computedAccept = this.options.acceptedFormats?.join(', ');
  }

  #canBePreviewImage(fileResolvable: FileResolvable | undefined): boolean {
    return !!fileResolvable && typeof fileResolvable === 'string' && !fileResolvable.endsWith('.pdf');
  }

  #isFileTypeAccepted(file: File): boolean {
    return this.options.acceptedFormats!.includes(file.type as AcceptEnum);
  }

  #isFileSizeLimitExceeded(file: File): boolean {
    return file.size / 1024 / 1024 > this.options.maxSizePerFileInMB!;
  }

  #clearNullControl(): void {
    const index = this.filesControlsArray.controls.findIndex((control) => control.value == null);
    if (index >= 0) {
      this.filesControlsArray.removeAt(index);
    }
  }
}
